"use client";

import {JSX} from "react";

import {useTranslation} from "@/shared/i18n";
import {Typography} from "@/shared/ui/data-display/typography";

import {MONTH_KEYS} from "../../constants";
import {DateRangeFilterTrigger} from "./triggers";

import "./styles/daterange-availability-header.scss";

export interface DateRangeAvailabilityHeaderProps {
    startDate: Date | null;
    endDate: Date | null;
    nightsCount: number | null;
    onClear: () => void;
    showNightsSummary?: boolean;
    showDateInputs?: boolean;
    formatDateRange?: (start: Date, end: Date) => string;
}

export function DateRangeAvailabilityHeader({
    startDate,
    endDate,
    nightsCount,
    onClear,
    showNightsSummary = true,
    showDateInputs = true,
    formatDateRange,
}: DateRangeAvailabilityHeaderProps): JSX.Element {
    const {t} = useTranslation("calendar");
    const {t: tRental} = useTranslation("rental");
    const {t: tBooking} = useTranslation("booking");

    /**
     * Format date using translated short month names
     */
    const formatDate = (date: Date): string => {
        const monthKey = MONTH_KEYS[date.getMonth()];
        // Use short month name (e.g., "januaryShort" -> "Jan")
        const shortMonthKey = `${monthKey}Short` as Parameters<typeof t>[0];
        const shortMonth = t(shortMonthKey);
        return `${shortMonth} ${date.getDate()}, ${date.getFullYear()}`;
    };

    const defaultFormatDateRange = (start: Date, end: Date): string => {
        return `${formatDate(start)} - ${formatDate(end)}`;
    };

    const formattedRange = startDate && endDate ? (formatDateRange || defaultFormatDateRange)(startDate, endDate) : null;

    const hasNoDates = !startDate && !endDate;
    const hasOnlyStartDate = startDate && !endDate;
    const hasBothDates = startDate && endDate;

    return (
        <div className="daterange-availability-header">
            {/* Left side: Nights summary or placeholder */}
            {showNightsSummary && (
                <div className="daterange-availability-header__summary">
                    {hasNoDates ? (
                        <>
                            <Typography as="div" size={18} weight={700} color="title" className="daterange-availability-header__nights">
                                {t("selectDates")}
                            </Typography>
                            <Typography as="div" size={12} weight={400} color="paragraph" className="daterange-availability-header__range">
                                {t("addTravelDates")}
                            </Typography>
                        </>
                    ) : hasOnlyStartDate ? (
                        <>
                            <Typography as="div" size={16} weight={700} color="title" className="daterange-availability-header__nights">
                                {tRental("nights", {count: 0})}
                            </Typography>
                            <Typography as="div" size={14} weight={400} color="paragraph" className="daterange-availability-header__range">
                                {formatDate(startDate)}
                            </Typography>
                        </>
                    ) : hasBothDates ? (
                        <>
                            <Typography as="div" size={16} weight={700} color="title" className="daterange-availability-header__nights">
                                {nightsCount} {tRental("nights", {count: nightsCount || 0})}
                            </Typography>
                            <Typography as="div" size={14} weight={400} color="paragraph" className="daterange-availability-header__range">
                                {formattedRange}
                            </Typography>
                        </>
                    ) : null}
                </div>
            )}

            {/* Right side: Date trigger */}
            {showDateInputs && (
                <div className="daterange-availability-header__controls">
                    <DateRangeFilterTrigger
                        startDate={startDate}
                        endDate={endDate}
                        startPlaceholder={tBooking("checkIn")}
                        endPlaceholder={tBooking("checkOut")}
                        disabled={false}
                        hasValue={!!(startDate || endDate)}
                        isOpen={false}
                        isLoading={false}
                        onClick={() => {
                            /* empty */
                        }}
                        onClear={onClear}
                        formatDisplayValue={() => ""}
                    />
                </div>
            )}
        </div>
    );
}
